import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { FolderPlus } from "lucide-react";
import { Button } from "../components/ui/button";
import { ProjectForm } from "../components/ProjectForms";
import { FolderKanban, ListTodo, AlertTriangle, Layers, RotateCcw, CheckCircle2, Target, Trophy, Users, PhoneCall } from "lucide-react";
import { useApi, KpiCard, Card, Loading, StatusBadge, PageHeader, Empty } from "../components/common";
import { useAuth } from "../context/AuthContext";
import { fdate, inr, num, pct, todayIST } from "../lib/format";
import { FollowUpList } from "./FollowUps";

function DesignerHome({ d }) {
  const t = todayIST();
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <KpiCard testid="my-active-projects" label="My Active Projects" value={d.stats.active_projects} icon={FolderKanban} tone="revenue" />
        <KpiCard testid="my-open-tasks" label="Open Tasks" value={d.stats.open_tasks} icon={ListTodo} tone="pending" />
        <KpiCard testid="my-overdue-tasks" label="Overdue Tasks" value={d.stats.overdue_tasks} icon={AlertTriangle} tone="expense" />
        <KpiCard testid="my-open-revisions" label="Open Revisions" value={d.stats.revisions_open} icon={RotateCcw} tone="expense" />
        <KpiCard testid="my-sheets-30d" label="Sheets Done (30d)" value={num(d.stats.sheets_completed_30d)} icon={Layers} tone="profit" />
        <KpiCard testid="my-tasks-30d" label="Tasks Done (30d)" value={d.stats.tasks_completed_30d} icon={CheckCircle2} tone="cash" />
      </div>
      <div className="grid lg:grid-cols-2 gap-4 mt-6">
        <Card title="My Projects" testid="my-projects">
          {d.projects.length ? d.projects.map((p) => (
            <Link key={p.id} to={`/projects/${p.id}`} className="flex items-center justify-between py-2.5 border-b last:border-0 hover:bg-violet-50/50 -mx-2 px-2 rounded" data-testid={`my-project-${p.id}`}>
              <div className="min-w-0"><div className="text-sm font-medium truncate">{p.name}</div><div className="text-xs text-muted-foreground">{p.album_size || "—"} · {p.total_sheets} sheets · due {fdate(p.deadline)}</div></div>
              <div className="flex items-center gap-2"><StatusBadge value={p.priority} /><StatusBadge value={p.status} /></div>
            </Link>
          )) : <Empty text="No active projects assigned to you" />}
        </Card>
        <Card title="My Tasks" testid="my-tasks">
          {d.tasks.length ? d.tasks.map((x) => (
            <Link key={x.id} to={`/projects/${x.project_id}`} className="flex items-center justify-between py-2.5 border-b last:border-0 hover:bg-violet-50/50 -mx-2 px-2 rounded">
              <div className="min-w-0"><div className="text-sm font-medium truncate">{x.name}</div><div className={`text-xs ${x.due_date && x.due_date < t ? "text-rose-600 font-semibold" : "text-muted-foreground"}`}>{x.project_name} · due {fdate(x.due_date)}</div></div>
              <StatusBadge value={x.status} />
            </Link>
          )) : <Empty text="No open tasks" />}
        </Card>
      </div>
    </>
  );
}

function SalesHome({ d }) {
  const p = d.performance;
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  return (
    <>
      <div className="vmw-card p-4 mb-4 flex flex-wrap items-center gap-3" data-testid="sales-create-project-card">
        <FolderPlus className="h-5 w-5 text-primary" />
        <div className="flex-1 min-w-[200px]"><div className="font-semibold text-sm">Confirmed a sale?</div><div className="text-xs text-muted-foreground">Create the project and assign a designer right away. They are notified instantly.</div></div>
        <Button className="vmw-gradient" onClick={() => setOpen(true)} data-testid="sales-create-project">Create New Project</Button>
      </div>
      <ProjectForm open={open} onClose={() => setOpen(false)} onSaved={(pr) => nav(`/projects/${pr.id}`)} />
      {p && (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
          <KpiCard testid="sales-leads-month" label="Leads This Month" value={p.leads} icon={Users} tone="revenue" />
          <KpiCard testid="sales-open-leads" label="Open Leads" value={p.open_leads} icon={PhoneCall} tone="pending" />
          <KpiCard testid="sales-won" label="Won This Month" value={p.won} icon={Trophy} tone="cash" />
          <KpiCard testid="sales-won-value" label="Won Value" value={inr(p.won_value)} icon={Trophy} tone="profit" />
          <KpiCard testid="sales-conversion" label="Conversion" value={pct(p.conversion_pct)} icon={Target} tone="revenue" />
          <KpiCard testid="sales-target" label="Target Achieved" value={pct(p.target_pct)} hint={p.target ? `of ${inr(p.target)}` : "no target set"} icon={Target} tone="profit" />
        </div>
      )}
      <div className="mt-6"><FollowUpList data={d.followups} /></div>
    </>
  );
}

export default function MyDashboard() {
  const { user } = useAuth();
  const { data, loading } = useApi("/dashboard/me");
  return (
    <div data-testid={`${user.role}-dashboard`}>
      <PageHeader title={`Welcome, ${user.name.split(" ")[0]}`} subtitle={user.role === "designer" ? "Your assigned projects and tasks" : "Your leads, follow-ups and sales performance"} />
      {loading || !data ? <Loading /> : data.role === "designer" ? <DesignerHome d={data} /> : <SalesHome d={data} />}
    </div>
  );
}
